/**
 * How much room a hanging peel tag needs UNDER the card it hangs from.
 *
 * Plain TypeScript - no React import. The card that hangs a tag caps its own height against the
 * viewport, and that cap has to know what is dangling below it: a card sized as if nothing hung there
 * pushes the tag past the fold, where the browser simply stops hit-testing. That is how
 * kitecorp/transferify-ui#2485 reads on screen - the invite tag's action ends exactly at the viewport's
 * bottom edge and the lower half of its touch target is not there.
 *
 * MEASURED rather than stated as a number: the paper's height is its copy, so it differs per language
 * (Romanian and German wrap where English does not) and per tag family. A constant would be correct in
 * one locale and wrong in the next, which is the defect this exists to remove.
 */

/**
 * The touch target `TagAction` grows past its own pill, top and bottom (`::before { inset: -4px 0 }`).
 *
 * Named here because it is the part of the tag that is INVISIBLE: a fit rule measuring only painted
 * boxes leaves exactly this much of the control under the fold, and every screenshot looks right.
 */
export const TAG_ACTION_HIT_BLEED_PX = 4;

/**
 * The reserve, from the tag row's measured box and the gap it hangs below the card by.
 *
 * Rounded UP, because a fractional layout that rounds the other way puts the last pixel of the touch
 * target outside. Zero when nothing hangs: a card with no tag keeps the whole height it had, and so
 * does a card whose tag sits beside it or over it, out of the flow (ui#2949).
 */
export function hangingTagReserve(input: {
	/** The tag row's border box, as `getBoundingClientRect().height` reports it. */
	rowHeight: number;
	/** The string gap between the card's lower edge and the paper - `TagPosition`'s top margin. */
	gapAbove: number;
	/** The row is positioned beside or over the card rather than stacked under it. */
	outOfFlow: boolean;
}): number {
	if (input.outOfFlow || input.rowHeight <= 0) {
		return 0;
	}
	return Math.ceil(input.rowHeight + Math.max(input.gapAbove, 0) + TAG_ACTION_HIT_BLEED_PX);
}
